import { useState } from 'react'
import styled from 'styled-components'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { Tabs } from 'antd'
import { useSelector } from 'react-redux'
import { useQueryClient } from 'react-query'
import {
  PageHeader,
  FilterContainer,
  RangePicker,
  GroupedResidentsMultiSelect,
  Table,
  Avatar,
  Button,
  Tag
} from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { NAMESPACE } from 'locale'
import { Home } from '@lmc/ts-common'
import useResidentsList from '../../../../Rostering/hooks/useResidentsList'
import PageWrapper from '../componentsV2/PageWrapper'
import Warning from '../iconsV2/Warning'
import Success from '../iconsV2/Success'
import { useStockDiscrepancies } from '../hooks/useStockDiscrepancies'
import { DiscrepancyRecord, DiscrepancyStatus } from '../types'
import ResolveDiscrepancyModal from '../componentsV2/modalsV2/ResolveDiscrepancyModal'
import { StockRecordDrawer } from '../componentsV2/StockRecordDrawer/StockRecordDrawer'
import { transformDiscrepancyRecordToStockRecord } from '../utils/stockRecordUtils'
import { StockRecord, MedicationStockReason } from '../types'
import StockCheckModal from '../componentsV2/modalsV2/StockCheckModal'
import AddStockModal from '../componentsV2/modalsV2/AddStockModal'
import DisposalModal from '../componentsV2/modalsV2/DisposalModal'
import { STOCK_DISCREPANCIES_QUERY_KEY } from '../hooks/queryConstants'

export const StockDiscrepanciesReportScreenV2 = () => {
  const { t } = useTranslation(NAMESPACE.EMAR_V2)
  const queryClient = useQueryClient()
  const home = useSelector(
    (state: { global: { home: Home } }) => state.global.home
  )
  const preferredPeopleNominative = home?.preferredPeopleNominative
  const { residents } = useResidentsList({})

  const [activeTab, setActiveTab] = useState('open')
  const [startDate, setStartDate] = useState(
    dayjs().subtract(30, 'days').startOf('day')
  )
  const [endDate, setEndDate] = useState(dayjs().endOf('day'))
  const [selectedResidents, setSelectedResidents] = useState<string[]>([])
  const [isResolveModalOpen, setIsResolveModalOpen] = useState(false)
  const [selectedRecord, setSelectedRecord] =
    useState<DiscrepancyRecord | null>(null)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [selectedDiscrepancyRecord, setSelectedDiscrepancyRecord] =
    useState<DiscrepancyRecord | null>(null)
  const [selectedStockRecord, setSelectedStockRecord] = useState<
    StockRecord | undefined
  >(undefined)
  const [selectedMedicationName, setSelectedMedicationName] = useState('')
  const [selectedControlledDrug, setSelectedControlledDrug] = useState(false)
  const [stockCheckModalOpen, setStockCheckModalOpen] = useState(false)
  const [addStockModalOpen, setAddStockModalOpen] = useState(false)
  const [disposalModalOpen, setDisposalModalOpen] = useState(false)
  const [editingRecord, setEditingRecord] = useState<StockRecord | null>(null)
  const [isEditMode, setIsEditMode] = useState(false)

  const statusMap: Record<string, 'PENDING' | 'RECONCILED' | undefined> = {
    open: 'PENDING',
    resolved: 'RECONCILED'
  }

  const { data: discrepanciesData, isLoading } = useStockDiscrepancies({
    startDate,
    endDate,
    status: statusMap[activeTab],
    residentId: selectedResidents.length > 0 ? selectedResidents : undefined
  })

  const handleDateChange = (dates: any) => {
    if (dates && dates[0] && dates[1]) {
      setStartDate(dates[0].startOf('day'))
      setEndDate(dates[1].endOf('day'))
    }
  }

  const handleResolveClick = (record: DiscrepancyRecord) => {
    setSelectedRecord(record)
    setIsResolveModalOpen(true)
  }

  const handleResolveSuccess = () => {
    setIsResolveModalOpen(false)
    setSelectedRecord(null)
  }

  const handleResolveCancel = () => {
    setIsResolveModalOpen(false)
    setSelectedRecord(null)
  }

  const handleRecordClick = (record: DiscrepancyRecord) => {
    const stockRecord = transformDiscrepancyRecordToStockRecord(record)
    setSelectedDiscrepancyRecord(record)
    setSelectedStockRecord(stockRecord)
    setSelectedMedicationName(record.medicationName)
    setSelectedControlledDrug(record.controlledDrug)
    setIsDrawerOpen(true)
  }

  const handleDrawerClose = () => {
    setIsDrawerOpen(false)
    setSelectedStockRecord(undefined)
    setSelectedDiscrepancyRecord(null)
  }

  const handleEditFromDrawer = (
    record: StockRecord,
    actionType?: MedicationStockReason
  ) => {
    if (!actionType) return
    setIsEditMode(true)
    setEditingRecord(record)
    switch (actionType) {
      case MedicationStockReason.STOCK_COUNT:
      case MedicationStockReason.RECONCILIATION:
      case MedicationStockReason.INITIAL_STOCK:
        setStockCheckModalOpen(true)
        break
      case MedicationStockReason.RESTOCK:
        setAddStockModalOpen(true)
        break
      case MedicationStockReason.DISPOSAL:
        setDisposalModalOpen(true)
        break
      default:
        break
    }
  }

  const expectedStock =
    editingRecord?.previousStockLevel ??
    editingRecord?.expectedStock ??
    selectedDiscrepancyRecord?.previousStockLevel ??
    0
  const unit = editingRecord?.unit ?? selectedDiscrepancyRecord?.unit ?? ''
  const residentId = selectedDiscrepancyRecord?.resident?.id ?? ''
  const residentMedicationId =
    selectedDiscrepancyRecord?.residentMedicationId ?? ''
  const pastRecordsStartDate = dayjs(selectedDiscrepancyRecord?.date)
    .subtract(14, 'days')
    .format('YYYY-MM-DD')
  const pastRecordsEndDate = dayjs(selectedDiscrepancyRecord?.date).format(
    'YYYY-MM-DD'
  )
  const pastRecordsUrl = selectedDiscrepancyRecord?.residentMedicationId
    ? `/residents/${selectedDiscrepancyRecord.resident.id}/emar/${selectedDiscrepancyRecord.residentMedicationId}/stock?startDate=${pastRecordsStartDate}&endDate=${pastRecordsEndDate}`
    : undefined

  const tableColumns = [
    {
      title: t('TIME_RECORDED'),
      dataIndex: 'date',
      key: 'date',
      width: 156,
      render: (_: any, record: DiscrepancyRecord) => (
        <Text>{dayjs(record.date).format('DD-MM-YY HH:mm')}</Text>
      )
    },
    {
      title: preferredPeopleNominative,
      dataIndex: 'resident',
      key: 'resident',
      width: 200,
      render: (_: any, record: DiscrepancyRecord) => {
        const residentName = `${record.resident.name.first} ${record.resident.name.last}`
        return (
          <ServiceUserCell>
            <AvatarContainer>
              <Avatar
                url={record.resident.picture as unknown as string}
                size={30}
              />
            </AvatarContainer>
            <Text>{residentName}</Text>
          </ServiceUserCell>
        )
      }
    },
    {
      title: t('MEDICATION'),
      dataIndex: 'medicationName',
      key: 'medicationName',
      render: (_: any, record: DiscrepancyRecord) => (
        <MedicationCell>
          <Text>{record.medicationName}</Text>
          <BadgeContainer>
            {record.prn && <Tag color="blue">{t('PRN')}</Tag>}
            {record.controlledDrug && <Tag color="red">{t('CD')}</Tag>}
          </BadgeContainer>
        </MedicationCell>
      )
    },
    {
      title: t('STOCK_COUNT').replace(' ', '\n'),
      dataIndex: 'resultingStockLevel',
      key: 'resultingStockLevel',
      width: 88,
      align: 'center' as const,
      render: (_: any, record: DiscrepancyRecord) => {
        const isOpen = record.discrepancy.status === DiscrepancyStatus.OPEN
        return (
          <StockCountCell>
            <Text>{record.resultingStockLevel}</Text>
            {isOpen ? (
              <IconWrapper>
                <Warning />
              </IconWrapper>
            ) : (
              <IconWrapper>
                <Success />
              </IconWrapper>
            )}
          </StockCountCell>
        )
      }
    },
    {
      title: t('EXPECTED_STOCK').replace(' ', '\n'),
      dataIndex: 'previousStockLevel',
      key: 'previousStockLevel',
      width: 88,
      align: 'center' as const,
      render: (_: any, record: DiscrepancyRecord) => (
        <Text> {record.previousStockLevel || 0}</Text>
      )
    },
    {
      title: activeTab === 'resolved' ? t('DISCREPANCY_REASON') : '',
      dataIndex: 'discrepancy',
      key: 'discrepancy',
      render: (_: any, record: DiscrepancyRecord) => {
        const isOpen = record.discrepancy.status === 'Open'
        const reason = !isOpen && record.discrepancy.reason
        return (
          <ReasonCell>
            {reason ? (
              <Text>{reason}</Text>
            ) : (
              <ResolveButton
                variant="default"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation()
                  handleResolveClick(record)
                }}
              >
                {t('RESOLVE_DISCREPANCY')}
              </ResolveButton>
            )}
          </ReasonCell>
        )
      }
    }
  ]

  const tabItems = [
    {
      key: 'open',
      label: <Text>{t('OPEN_DISCREPANCIES')}</Text>,
      children: (
        <StyledTable
          columns={tableColumns as any}
          dataSource={discrepanciesData?.records || []}
          rowKey="_id"
          pagination={{
            pageSize: 10,
            showSizeChanger: false
          }}
          loading={isLoading}
          onRow={(record) => ({
            onClick: () => handleRecordClick(record as DiscrepancyRecord),
            style: { cursor: 'pointer' }
          })}
        />
      )
    },
    {
      key: 'resolved',
      label: <Text>{t('RESOLVED_DISCREPANCIES')}</Text>,
      children: (
        <StyledTable
          columns={tableColumns as any}
          dataSource={discrepanciesData?.records || []}
          rowKey="_id"
          pagination={{
            pageSize: 10,
            showSizeChanger: false
          }}
          loading={isLoading}
          onRow={(record) => ({
            onClick: () => handleRecordClick(record as DiscrepancyRecord),
            style: { cursor: 'pointer' }
          })}
        />
      )
    }
  ]

  return (
    <>
      <PageHeader title={t('STOCK_DISCREPANCIES')} sticky />
      <PageWrapper>
        <FilterContainer title="">
          <>
            <RangePicker
              defaultValue={[startDate, endDate]}
              value={[startDate, endDate]}
              onChange={handleDateChange}
              format="DD-MM-YYYY"
            />
            <GroupedResidentsMultiSelect
              residents={residents}
              title={preferredPeopleNominative || ''}
              values={selectedResidents}
              handleInputChange={setSelectedResidents}
              hasClear
              hasSearch
              hasSelectAll
            />
          </>
        </FilterContainer>
        <StyledTabs
          defaultActiveKey="open"
          activeKey={activeTab}
          items={tabItems}
          onChange={setActiveTab}
        />
      </PageWrapper>
      <ResolveDiscrepancyModal
        open={isResolveModalOpen}
        stockRecordId={selectedRecord?._id || ''}
        medicationName={selectedRecord?.medicationName}
        onCancel={handleResolveCancel}
        onSuccess={handleResolveSuccess}
      />
      <StockRecordDrawer
        isVisible={isDrawerOpen}
        onClose={handleDrawerClose}
        record={selectedStockRecord}
        medicationName={selectedMedicationName}
        controlledDrug={selectedControlledDrug}
        onEdit={handleEditFromDrawer}
        pastRecordsUrl={pastRecordsUrl}
        trackStock={selectedDiscrepancyRecord?.trackStock}
      />
      {stockCheckModalOpen && (
        <StockCheckModal
          open={stockCheckModalOpen}
          medicationName={selectedMedicationName}
          expectedStock={expectedStock}
          residentId={residentId}
          trackStock={selectedDiscrepancyRecord?.trackStock}
          residentMedicationId={residentMedicationId}
          unit={unit}
          onCancel={() => {
            setStockCheckModalOpen(false)
            setIsEditMode(false)
            setEditingRecord(null)
            handleDrawerClose()
          }}
          onSuccess={() => {
            setStockCheckModalOpen(false)
            setIsEditMode(false)
            setEditingRecord(null)
            handleDrawerClose()
            queryClient.invalidateQueries([STOCK_DISCREPANCIES_QUERY_KEY])
          }}
          editMode={isEditMode}
          record={isEditMode ? (editingRecord ?? undefined) : undefined}
        />
      )}
      {addStockModalOpen && (
        <AddStockModal
          open={addStockModalOpen}
          medicationName={selectedMedicationName}
          expectedStock={expectedStock}
          residentId={residentId}
          residentMedicationId={residentMedicationId}
          unit={unit}
          onCancel={() => {
            setAddStockModalOpen(false)
            setIsEditMode(false)
            setEditingRecord(null)
            handleDrawerClose()
          }}
          onSuccess={() => {
            setAddStockModalOpen(false)
            setIsEditMode(false)
            setEditingRecord(null)
            handleDrawerClose()
            queryClient.invalidateQueries([STOCK_DISCREPANCIES_QUERY_KEY])
          }}
          editMode={isEditMode}
          record={isEditMode ? (editingRecord ?? undefined) : undefined}
        />
      )}
      {disposalModalOpen && (
        <DisposalModal
          open={disposalModalOpen}
          medicationName={selectedMedicationName}
          expectedStock={expectedStock}
          residentId={residentId}
          residentMedicationId={residentMedicationId}
          unit={unit}
          onCancel={() => {
            setDisposalModalOpen(false)
            setIsEditMode(false)
            setEditingRecord(null)
            handleDrawerClose()
          }}
          onSuccess={() => {
            setDisposalModalOpen(false)
            setIsEditMode(false)
            setEditingRecord(null)
            handleDrawerClose()
            queryClient.invalidateQueries([STOCK_DISCREPANCIES_QUERY_KEY])
          }}
          editMode={isEditMode}
          record={isEditMode ? (editingRecord ?? undefined) : undefined}
        />
      )}
    </>
  )
}

const AvatarContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
`

const ServiceUserCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const MedicationCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const BadgeContainer = styled.div`
  display: flex;
  gap: 4px;
`

const StockCountCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
`

const IconWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  width: 14px;
`

const ReasonCell = styled.div`
  display: flex;
  align-items: center;
`

const ResolveButton = styled(Button)`
  white-space: nowrap;
`

const StyledTable = styled(Table)`
  .ant-table-thead > tr > th {
    white-space: pre-line;
    text-align: center;
  }
`

const StyledTabs = styled(Tabs)`
  width: 100%;
  .ant-tabs-nav {
    margin: 0 0 24px 24px;
  }
`
